import React, { useEffect, useRef } from 'react';
import { Terminal } from 'lucide-react';

const ConsoleOutput = ({ job }) => {
  const consoleRef = useRef(null);

  // Auto-scroll to bottom when output changes
  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [job?.stdout, job?.stderr]);

  // Filter pytest output to show only failures and errors
  const filterPytestOutput = (output) => {
    if (!output) return '';
    
    const lines = output.split('\n');
    const filteredLines = [];
    let inFailureSection = false;
    let inWarningSection = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Start capturing at FAILURES section
      if (line.includes('=== FAILURES ===') || line.includes('=== ERRORS ===')) {
        inFailureSection = true;
        filteredLines.push(line);
        continue;
      }
      
      // Stop capturing at warnings summary or passed tests
      if (line.includes('=== warnings summary ===')) {
        inWarningSection = true;
        inFailureSection = false;
        continue;
      }
      
      if (line.includes('=== PASSED ===') || line.includes('passed in')) {
        inFailureSection = false;
        inWarningSection = false;
        continue;
      }
      
      // Capture lines in failure section
      if (inFailureSection) {
        filteredLines.push(line);
      }
    }
    
    // If no failures captured, show raw output (for other errors)
    if (filteredLines.length === 0) {
      return output;
    }
    
    return filteredLines.join('\n');
  };

  const getConsoleContent = () => {
    const stdout = job?.stdout || '';
    const stderr = job?.stderr || '';
    
    // Combine and filter
    const combined = stdout + (stderr ? '\n' + stderr : '');
    return filterPytestOutput(combined);
  };

  const consoleContent = getConsoleContent();
  const isRunning = job?.status === 'running' || job?.status === 'pending' || job?.status === 'queued';

  return (
    <div className="h-full flex flex-col bg-gray-900">
      <div className="flex items-center px-3 py-2 bg-gray-800 border-b border-gray-700">
        <Terminal className="h-4 w-4 text-gray-400 mr-2" />
        <span className="text-xs font-medium text-gray-300 uppercase tracking-wide">Console Output</span>
        {isRunning && (
          <span className="ml-auto text-xs text-blue-400 animate-pulse">Running...</span>
        )}
      </div>
      
      <div 
        ref={consoleRef}
        className="flex-1 overflow-y-auto p-3 font-mono text-xs text-gray-100 whitespace-pre-wrap"
      >
        {consoleContent || (
          <div className="text-gray-500 italic">
            {isRunning ? 'Waiting for output...' : 'No console output'}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsoleOutput;
